
// src/screens/admin/categories/Categories.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { Row, Col } from "react-bootstrap";
import {
  FaSearch,
  FaPlus,
  FaTimes,
  FaUpload,
  FaFolder,
  FaCheckCircle,
  FaFolderOpen,
} from "react-icons/fa";
import Button from "../../../components/general/button/Button";
import Pagination from "../../../components/general/pagination/Pagination.js";
import ActionDropdown from "../../../components/admin/dropdown/action/ActionDropdown.js";
import StatCard from "../../../components/admin/statcard/StatCard.js";
import "../../../assets/css/admin/categories/Categories.css";
import AdminLayout from "../../../layouts/AdminLayout.js";
import MyContext from "../../../context/MyContext.js";
import { CategoriesListSkeleton } from "../../../components/general/skeleton/SkeletonBlock";

const CategoriesList = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(MyContext);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingSubcategory, setIsEditingSubcategory] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [categoriesList, setCategoriesList] = useState([]);
  const [subcategoriesList, setSubcategoriesList] = useState([]);
  const [totalCategoriesCount, setTotalCategoriesCount] = useState(0);
  const [activeCategoriesCount, setActiveCategoriesCount] = useState(0);
  const [totalSubcategoriesCount, setTotalSubcategoriesCount] = useState(0);

  // Category States
  const [categoryname, setCategoryName] = useState("");
  const [categoryslug, setCategorySlug] = useState("");
  const [categorystatus, setCategoryStatus] = useState(1);
  const [idToUpdateDeleteCategory, setIdToUpdateDeleteCategory] = useState("");

  // Subcategory States
  const [subcategoryimage, setSubCategoryimage] = useState([0]);
  const [subcategoryname, setSubcategoryName] = useState("");
  const [subcategoryslug, setSubcategorySlug] = useState("");
  const [subcategorystatus, setSubCategoryStatus] = useState(1);
  const [idToUpdateDeleteSubcategory, setIdToUpdateDeleteSubcategory] =
    useState("");

  //////////Category Api's

  const viewAllCategories = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/category/viewAllCategories`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setCategoriesList(response.data.category);
          setTotalCategoriesCount(response.data.totalCategories ?? 0);
          setActiveCategoriesCount(response.data.activeCategories ?? 0);
          setTotalSubcategoriesCount(response.data.totalSubcategories ?? 0);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load categories.",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/admin/login");
      return;
    }
    viewAllCategories();
  }, [authToken]);

  const addCategory = () => {
    const formData = new FormData();
    formData.append("categoryname", categoryname);
    formData.append("categoryslug", categoryslug);
    formData.append("categorystatus", categorystatus ? 1 : 0);
    axios
      .post(`${baseUrl}/admin/category/addCategory`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Category added successfully!",
          });
          setShowAddModal(false);
          viewAllCategories();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add category.",
        });
      });
  };

  const updateCategory = () => {
    const formData = new FormData();
    formData.append("id", idToUpdateDeleteCategory);
    formData.append("categoryname", categoryname);
    formData.append("categoryslug", categoryslug);
    formData.append("categorystatus", categorystatus ? 1 : 0);
    axios
      .post(`${baseUrl}/admin/category/updateCategory`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Category updated successfully!",
          });
          setShowAddModal(false);
          viewAllCategories();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update category.",
        });
      });
  };

  const deleteCategory = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This category will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", idToUpdateDeleteCategory);
        axios
          .post(`${baseUrl}/admin/category/deleteCategory`, formData, {
            headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            if (response.data.hasOwnProperty("error")) {
              setErrorMessage(response.data.error);
              Swal.fire({
                icon: "error",
                title: "Error",
                text: response.data.error,
              });
            } else {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Category deleted successfully!",
              });
              viewAllCategories();
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete category.",
            });
          });
      }
    });
  };

  const toggleCategoryStatus = (id, currentStatus) => {
    const formData = new FormData();
    formData.append("id", id);
    formData.append("categorystatus", currentStatus ? 0 : 1);
    axios
      .post(`${baseUrl}/admin/category/updateCategory`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          viewAllCategories();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update status.",
        });
      });
  };

  ///////////////Subcategory Api's
const viewAllSubcategories = (categoryId) => {
    console.log("Category ID:", categoryId);
    const formData = new FormData();
    formData.append("kmcategory_id", categoryId); 
    axios
      .post(`${baseUrl}/admin/subcategory/viewAllSubcategories`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log("Full Response:", response.data);
        if (response.data.success == true) {
          setSubcategoriesList(response.data.subcategory);
        } else if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({ icon: "error", title: "Error", text: response.data.error });
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load subcategories." });
      });
};
  const addSubcategory = () => {
    const formData = new FormData();
    formData.append("subcategoryname", subcategoryname);
    formData.append("subcategoryslug", subcategoryslug);
    formData.append("subcategorystatus", subcategorystatus ? 1 : 0);
    formData.append("subcategoryimage", subcategoryimage);
    formData.append("kmcategory_id", selectedCategory.id);
    axios
      .post(`${baseUrl}/admin/subcategory/addSubcategory`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Subcategory added successfully!",
          });
          setShowSubcategoryModal(false);
          viewAllSubcategories(selectedCategory.id);
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to add subcategory.",
        });
      });
  };

  const updateSubcategory = () => {
    const formData = new FormData();
    formData.append("id", idToUpdateDeleteSubcategory);
    formData.append("subcategoryname", subcategoryname);
    formData.append("subcategoryimage", subcategoryimage);
    axios
      .post(`${baseUrl}/admin/subcategory/updateSubcategory`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Subcategory updated successfully!",
          });
          setShowSubcategoryModal(false);
          viewAllSubcategories(selectedCategory.id);
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update subcategory.",
        });
      });
  };

  const deleteSubcategory = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This subcategory will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("id", idToUpdateDeleteSubcategory);
        axios
          .post(`${baseUrl}/admin/subcategory/deleteSubcategory`, formData, {
            headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
          })
          .then((response) => {
            if (response.data.hasOwnProperty("error")) {
              setErrorMessage(response.data.error);
              Swal.fire({
                icon: "error",
                title: "Error",
                text: response.data.error,
              });
            } else {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Subcategory deleted successfully!",
              });
              viewAllSubcategories(selectedCategory.id);
            }
          })
          .catch((error) => {
            console.log(error);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete subcategory.",
            });
          });
      }
    });
  };

  const toggleSubcategoryStatus = (id, currentStatus) => {
    const formData = new FormData();
    formData.append("id", id);
    formData.append("subcategorystatus", currentStatus ? 0 : 1);
    axios
      .post(`${baseUrl}/admin/subcategory/updateSubcategory`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          viewAllSubcategories(selectedCategory.id);
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update status.",
        });
      });
  };

  // ─── Slug Generator ───────────────────────────────────────────────────────────

  const generateSlug = (value) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // ─── Stats ────────────────────────────────────────────────────────────────────

  const statsData = [
    {
      icon: FaFolder,
      iconBg: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
      label: "Total Categories",
      value: totalCategoriesCount.toString(),
      badge: "All",
    },
    {
      icon: FaCheckCircle,
      iconBg: "linear-gradient(135deg, #e8f5f0, #d4ede3)",
      label: "Active Main",
      value: activeCategoriesCount.toString(),
      badge: "Live",
    },
    {
      icon: FaFolderOpen,
      iconBg: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
      label: "Total Sub-Categories",
      value: totalSubcategoriesCount.toString(),
      badge: "Active",
    },
  ];

  // ─── Filter & Actions ─────────────────────────────────────────────────────────

  const filteredCategories = categoriesList.filter(
    (cat) =>
      cat.categoryname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cat.categoryslug.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getActions = (category) => [
    {
      icon: "MdVisibility",
      label: "View Details",
      onClick: () => {
        setSelectedCategory(category);
        viewAllSubcategories(category.id);
        setShowViewModal(true);
      },
    },
    {
      icon: "MdAddCircle",
      label: "Add Subcategory",
      onClick: () => {
        setSelectedCategory(category);
        setIsEditingSubcategory(false);
        setSubcategoryName("");
        setSubcategorySlug("");
        setSubCategoryStatus(true);
        setSubCategoryimage(null);
        setIdToUpdateDeleteSubcategory("");
        setShowSubcategoryModal(true);
      },
    },
    {
      icon: "MdEdit",
      label: "Edit Category",
      onClick: () => {
        setIsEditing(true);
        setSelectedCategory(category);
        setIdToUpdateDeleteCategory(category.id);
        setCategoryName(category.categoryname);
        setCategorySlug(category.categoryslug);
        setCategoryStatus(category.categorystatus);
        setShowAddModal(true);
      },
    },
    {
      icon: category.categorystatus ? "MdToggleOff" : "MdToggleOn",
      label: category.categorystatus ? "Disable" : "Enable",
      onClick: () => toggleCategoryStatus(category.id, category.categorystatus),
    },
    {
      icon: "MdDelete",
      label: "Delete",
      onClick: () => {
        setIdToUpdateDeleteCategory(category.id);
        deleteCategory();
      },
      className: "danger",
    },
  ];

  const getSubcategoryActions = (subcategory) => [
    {
      icon: "MdEdit",
      label: "Edit Subcategory",
      onClick: () => {
        setIsEditingSubcategory(true);
        setIdToUpdateDeleteSubcategory(subcategory.id);
        setSubcategoryName(subcategory.subcategoryname);
        setSubcategorySlug(subcategory.subcategoryslug);
        setSubCategoryStatus(subcategory.subcategorystatus);
        setShowSubcategoryModal(true);
      },
    },
    {
      icon: subcategory.subcategorystatus ? "MdToggleOff" : "MdToggleOn",
      label: subcategory.subcategorystatus ? "Disable" : "Enable",
      onClick: () => toggleSubcategoryStatus(subcategory.id),
    },
    {
      icon: "MdDelete",
      label: "Delete",
      onClick: () => {
        setIdToUpdateDeleteSubcategory(subcategory.id);
        deleteSubcategory();
      },
      className: "danger",
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <CategoriesListSkeleton />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div className="ksa-category-page-header">
          <div>
            <h1 className="ksa-layout-page-title">Categories Management</h1>
            <p className="ksa-layout-page-subtitle">
              Organize and structure your marketplace product taxonomy.
            </p>
          </div>
          <Button
            variant="primary"
            size="medium"
            icon={<FaPlus />}
            iconPosition="left"
            onClick={() => {
              setIsEditing(false);
              setShowAddModal(true);
            }}
          >
            Add New Category
          </Button>
        </div>

        {/* Stats Cards */}
        <Row className="g-4 mb-4 ksa-category-stats">
          {statsData.map((stat, idx) => (
            <Col key={idx} xs={12} md={4}>
              <StatCard {...stat} />
            </Col>
          ))}
        </Row>

        {/* Main Table Card */}
        <div className="ksa-card">
          <div className="ksa-category-table-header">
            <div className="ksa-category-search">
              <FaSearch size={16} />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="ksa-category-table-wrapper">
          <table className="ksa-category-table">
            <thead>
              <tr>
                <th>CATEGORY NAME</th>
                <th>SLUG</th>
                <th style={{ textAlign: "center" }}>TOTAL SUBCATEGORIES</th>
                <th style={{ textAlign: "center" }}>TOTAL PRODUCTS</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredCategories
                .slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage,
                )
                .map((category) => (
                  <tr key={category.id}>
                    <td className="ksa-category-name">
                      {category.categoryname}
                    </td>
                    <td className="ksa-category-slug">
                      {category.categoryslug}
                    </td>
                    <td className="ksa-category-subcategories">
                      {category.subcategories
                        ? category.subcategories.length
                        : 0}
                    </td>
                    <td className="ksa-category-products text-center">
                      {category.totalProducts}
                    </td>
                    <td>
                      <label className="ksa-category-toggle">
                        <input
                          type="checkbox"
                          checked={category.categorystatus}
                          onChange={() =>
                            toggleCategoryStatus(
                              category.id,
                              category.categorystatus,
                            )
                          }
                        />
                        <span className="ksa-category-toggle-slider"></span>
                      </label>
                    </td>
                    <td>
                      <ActionDropdown actions={getActions(category)} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          </div>

          <Pagination
            totalItems={filteredCategories.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>

        {/* Add/Edit Category Modal */}
        {showAddModal && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowAddModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-md ksa-category-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-category-modal-header">
                <h3 className="ksa-category-modal-title">
                  {isEditing ? "Edit Category" : "Add New Category"}
                </h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowAddModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-category-modal-body">
                <div className="ksa-category-form-group">
                  <label>Category Name *</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    placeholder="e.g., Organic Seeds"
                    value={categoryname}
                    onChange={(e) => {
                      setCategoryName(e.target.value);
                      setCategorySlug(generateSlug(e.target.value));
                    }}
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Slug (URL) *</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    placeholder="e.g., organic-seeds"
                    value={categoryslug}
                    onChange={(e) => setCategorySlug(e.target.value)}
                  />
                  <small style={{ color: "#999", fontSize: "0.8rem" }}>
                    Auto-generated from category name
                  </small>
                </div>
              </div>

              <div className="ksa-category-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={isEditing ? updateCategory : addCategory}
                >
                  {isEditing ? "Update Category" : "Create Category"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* View Category Details Modal */}
        {showViewModal && selectedCategory && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowViewModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-lg ksa-category-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-category-modal-header">
                <h3 className="ksa-category-modal-title">
                  Category Details: {selectedCategory.categoryname}
                </h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowViewModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-category-modal-body">
                <Row className="g-4">
                  <Col xs={12} md={4}>
                    <div className="ksa-category-detail-info">
                      <div className="ksa-category-detail-item">
                        <label>Category Name</label>
                        <p>{selectedCategory.categoryname}</p>
                      </div>
                      <div className="ksa-category-detail-item">
                        <label>Slug</label>
                        <p>{selectedCategory.categoryslug}</p>
                      </div>
                      <div className="ksa-category-detail-item">
                        <label>Total Products</label>
                        <p>{selectedCategory.totalProducts}</p>
                      </div>
                      <div className="ksa-category-detail-item">
                        <label>Status</label>
                        <p
                          className={
                            selectedCategory.categorystatus
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {selectedCategory.categorystatus
                            ? "✓ Active"
                            : "✕ Inactive"}
                        </p>
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} md={8}>
                    <div className="ksa-category-subcategories-section">
                      <div className="ksa-category-subcategories-header">
                        <h4>Subcategories ({subcategoriesList.length})</h4>
                        <Button
                          variant="primary"
                          size="small"
                          icon={<FaPlus />}
                          iconPosition="left"
                          onClick={() => {
                            setIsEditingSubcategory(false);
                            setSubcategoryName("");
                            setSubcategorySlug("");
                            setSubCategoryStatus(true);
                            setSubCategoryimage(null);
                            setIdToUpdateDeleteSubcategory("");
                            setShowSubcategoryModal(true);
                          }}
                        >
                          Add New Subcategory
                        </Button>
                      </div>

                      {subcategoriesList.length > 0 ? (
                        <div className="ksa-category-subcategories-table-wrapper">
                        <table className="ksa-category-subcategories-table">
                          <thead>
                            <tr>
                              <th>NAME</th>
                              <th>SLUG</th>
                              <th>PRODUCTS</th>
                              <th>STATUS</th>
                              <th>ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subcategoriesList.map((subcat) => (
                              <tr key={subcat.id}>
                                <td className="ksa-category-subcat-name">
                                  {subcat.subcategoryname}
                                </td>
                                <td className="ksa-category-subcat-slug">
                                  {subcat.subcategoryslug}
                                </td>
                                <td className="ksa-category-subcat-products">
                                  {subcat.products}
                                </td>
                                <td>
                                  <label className="ksa-category-toggle">
                                    <input
                                      type="checkbox"
                                      checked={subcat.subcategorystatus}
                                      onChange={() =>
                                        toggleSubcategoryStatus(subcat.id)
                                      }
                                    />
                                    <span className="ksa-category-toggle-slider"></span>
                                  </label>
                                </td>
                                <td>
                                  <ActionDropdown
                                    actions={getSubcategoryActions(subcat)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      ) : (
                        <div className="ksa-category-no-subcategories">
                          <p>
                            No subcategories yet. Click "Add New Subcategory" to
                            create one.
                          </p>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="ksa-category-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Subcategory Modal */}
        {showSubcategoryModal && (
          <div
            className="ksa-modal-overlay"
            onClick={() => setShowSubcategoryModal(false)}
          >
            <div
              className="ksa-modal ksa-modal-md ksa-category-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ksa-category-modal-header">
                <h3 className="ksa-category-modal-title">
                  {isEditingSubcategory
                    ? "Edit Subcategory"
                    : "Add New Subcategory"}
                </h3>
                <button
                  className="ksa-modal-close"
                  onClick={() => setShowSubcategoryModal(false)}
                >
                  <FaTimes size={18} />
                </button>
              </div>

              <div className="ksa-category-modal-body">
                <div className="ksa-category-form-group">
                  <label>Parent Category</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    value={selectedCategory?.categoryname}
                    disabled
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Subcategory Name *</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    placeholder="e.g., Wheat Seeds"
                    value={subcategoryname}
                    onChange={(e) => {
                      setSubcategoryName(e.target.value);
                      setSubcategorySlug(generateSlug(e.target.value));
                    }}
                  />
                </div>

                <div className="ksa-category-form-group">
                  <label>Slug (URL) *</label>
                  <input
                    type="text"
                    className="ksa-category-input"
                    placeholder="e.g., wheat-seeds"
                    value={subcategoryslug}
                    onChange={(e) => setSubcategorySlug(e.target.value)}
                  />
                  <small style={{ color: "#999", fontSize: "0.8rem" }}>
                    Auto-generated from subcategory name
                  </small>
                </div>

                <div className="ksa-category-form-group">
                  <label>Subcategory Image</label>
                  <div className="ksa-category-upload-area">
                    <input
                      type="file"
                      id="subcategoryImage"
                      style={{ display: "none" }}
                      onChange={(e) => setSubCategoryimage(e.target.files[0])}
                    />
                    <label
                      htmlFor="subcategoryImage"
                      style={{ cursor: "pointer", width: "100%" }}
                    >
                      <FaUpload size={28} />
                      <p>
                        {subcategoryimage
                          ? subcategoryimage.name
                          : "Click to upload or drag and drop"}
                      </p>
                      <span>PNG, JPG (max 2MB)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="ksa-category-modal-footer">
                <Button
                  variant="secondary"
                  onClick={() => setShowSubcategoryModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={
                    isEditingSubcategory ? updateSubcategory : addSubcategory
                  }
                >
                  {isEditingSubcategory
                    ? "Update Subcategory"
                    : "Create Subcategory"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CategoriesList;
